import CandidateProvider, { useCandidate } from "../../../providers/CandidateProvider";
import { useProject } from "../../../providers/ClientProjectProvider";
import { usePosition } from "../../../providers/ProjectPositionProvider";
import React, { useMemo, useState } from "react";
import AnimatedNumber from "animated-number-react";
import { SimpleCircleProgress } from "../../../components/CircleProgress";
import { Collapse, Dropdown, Badge as ANTBadge } from "antd";
import CandidateProposal from "../../candidate-proposal/CandidateProposal";
import InfoTooltip from "../../../components/InfoTooltip";
import { Link, useNavigate } from "react-router-dom";
import { ClockCircleOutlined, RightOutlined, StopOutlined } from '@ant-design/icons'
import UserImageCircle from "../../../components/UserImageCircle";
import { useAuth } from "../../../providers/AuthProvider";
import HideTalentPricingTooltip from "../../clients/HideTalentPricingTooltip";
import TalentDisplayName from "../../../components/TalentDisplayName";

const ShortlistedCandidateRow = ({ onRequest, isActive, onViewProposal, ...props }) => {

    const {
        candidate,
        permissionToInvite,
        removeFromCandidates,
        assignCandidate,
        canMessage,
        isDisabled: isCandidateDisabled,
        canInterview,
        matchingDisciplines
    } = useCandidate();

    const navigate = useNavigate();
    const { position, isDisabled: isPositionDisabled } = usePosition();
    const { project } = useProject();
    const { auth } = useAuth();
    const isDisabled = isPositionDisabled || props.isDisabled || isCandidateDisabled
    const imageSrc = useMemo(() => {
        if (candidate?.image_path && String(candidate?.image_path).includes('default-user')) {
            return null;
        }
        return candidate?.image_path || TB.defaultUserProfileImagePath()
    }, [candidate?.id])

    if (!candidate.isShortlisted()) {
        return null;
    }

    return (
        <div className="flex flex-col lg:flex-row justify-between py-3 sm:py-0" style={{ pointerEvents: 'all' }}>
            <div className="flex-1 flex flex-row items-center gap-4">
                <div className="candidate-card-image hidden sm:block">
                    <UserImageCircle user={candidate} hideTooltip={true} size={110} className="img-fluid img-grayscale" />
                    {candidate.statusImage() && (
                        <img alt="" src={candidate.statusImage()} className="candidate-img-status" />
                    )}
                </div>
                <div className="candidate-user">
                    <div className="flex sm:block gap-4">
                        <div className="candidate-card-image sm:hidden">
                            <UserImageCircle user={candidate} hideTooltip={true} size={70} className="img-fluid img-grayscale" />
                            {candidate.statusImage() && (
                                <img alt="" src={candidate.statusImage()} className="candidate-img-status" />
                            )}
                        </div>
                        <div>

                            {/* <div className="flex flex-row items-center w-[210px] justify-start"> */}
                            <div className="flex flex-row items-center justify-start">

                                <TalentDisplayName talent={candidate}>
                                    <h6 className="truncate max-w-[200px]">
                                        {candidate.firstNamePlusInitial()}
                                    </h6>
                                </TalentDisplayName>

                                <Link
                                    className="btn btn-link btn-link-primary hover:underline w-[70px] whitespace-nowrap"
                                    to={{
                                        pathname: `talent-profiles/${candidate.id}`,
                                        search: position?.id ? `?role=${position?.id}` : ''
                                    }}
                                >
                                    View Profile
                                </Link>
                            </div>

                            <div className="flex flex-row items-center gap-3">
                                {candidate.location && (
                                    <p className="mb-0 whitespace-no-wrap overflow-hidden max-w-[140px] text-ellipsis">
                                        <i className="fa fa-map-marker-alt mr-1" />
                                        {candidate.location.city}{!!candidate.location.state && `, ${candidate.location.state}`}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="matched-roles">

                        {TB.map(TB.first(TB.chunk(matchingDisciplines, 2)), discipline => (
                            <p className={TB.mapClass("role2", {
                                'max-w-[260px]': matchingDisciplines?.length === 1,
                                'max-w-[155px]': matchingDisciplines?.length === 2
                            })} key={`r-${discipline.id}`}>
                                {discipline.name}
                            </p>
                        ))}
                        {matchingDisciplines?.length > 2 && (
                            <>
                                <p id={`matched-roles-${candidate.id}`}
                                    className="more-roles whitespace-nowrap">+ {TB.pluralize('role', matchingDisciplines?.length - 2, true)}</p>
                                <InfoTooltip
                                    type="dark"
                                    placement="top"
                                    target={`matched-roles-${candidate.id}`}
                                >
                                    {matchingDisciplines.slice(2).map(discipline => {
                                        return <p key={discipline.id}
                                            className="px-3 py-1 m-0 text-white text-left">{discipline.name}</p>
                                    })}
                                </InfoTooltip>
                            </>
                        )}
                    </div>

                    {candidate?.hasMatching() && (
                        <div className="flex flex-row items-center">
                            <SimpleCircleProgress
                                stroke={20}
                                width="18px"
                                className="mr-2"
                                value={candidate.overallMatchPercent()} />

                            <AnimatedNumber
                                className="font-semibold text-xs mb-0"
                                value={candidate.overallMatchPercent()}
                                formatValue={(value) => `${TB.round(value)}% Total Match`}
                                duration={1000} />
                        </div>
                    )}

                </div>

                <div className="candidate-btns m-auto lg:hidden flex flex-col items-center justify-center gap-4 self-stretch">
                    {/* {!project.isActive() && !position.hasBeenFilled() && !isActive && candidate.hasProposalForClient() && ( */}
                    {!project.isActive() && !position.hasBeenFilled() && candidate.hasProposalForClient() && (
                        <React.Fragment>
                            <ANTBadge count={candidate.unreadMessageCount()} size="small">
                                <button
                                    className="btn message-btn"
                                    disabled={isDisabled}
                                    onClick={() => onViewProposal()}
                                >
                                    <i className="far fa-comment-alt" />
                                </button>
                            </ANTBadge>
                            <ANTBadge count={candidate.hasInterviewUpdateSinceLastViewed() ? '!' : null} size="small">
                                <button
                                    className="btn interview-btn"
                                    disabled={isDisabled}
                                    onClick={() => onViewProposal('interviews')}
                                >
                                    <i className="far fa-calendar-alt" />
                                </button>
                            </ANTBadge>
                        </React.Fragment>
                    )}
                </div>

            </div>
            <div className="candidate-btns hidden lg:flex flex-col mx-auto min-w-[100px] items-center justify-center gap-4 self-stretch">
                {/* {!project.isActive() && !position.hasBeenFilled() && !isActive && candidate.hasProposalForClient() && ( */}
                {!project.isActive() && !position.hasBeenFilled() && candidate.hasProposalForClient() && (
                    <React.Fragment>
                        <ANTBadge count={candidate.unreadMessageCount()} size="small">
                            <button
                                className="btn message-btn"
                                disabled={isDisabled}
                                onClick={() => onViewProposal()}
                            >
                                <i className="far fa-comment-alt" />
                            </button>
                        </ANTBadge>
                        <ANTBadge count={candidate.hasInterviewUpdateSinceLastViewed() ? '!' : null} size="small">
                            <button
                                className="btn interview-btn"
                                disabled={isDisabled}
                                onClick={() => onViewProposal('interviews')}
                            >
                                <i className="far fa-calendar-alt" />
                            </button>
                        </ANTBadge>
                    </React.Fragment>
                )}
            </div>
            <div className="pt-4 lg:pt-0 flex-1 flex flex-row gap-5 items-center">

                <div className="candidate-rates">
                    {candidate.isAssigned() ? (
                        candidate.isTimeMaterials() ? (
                            <div className="selected-rate">
                                <h4>Accepted Rate</h4>
                                <h2>
                                    {auth.canViewTalentPricing() ? (<span>{candidate.proposedPlatformRate()}/hr</span>) : <HideTalentPricingTooltip />}
                                </h2>
                            </div>
                        ) : (candidate.isFixedRetainer() ? (
                            <div className="selected-rate">
                                <h4>Accepted Cost</h4>
                                {auth.canViewTalentPricing() ? (
                                    <>
                                        <h2>
                                            <span>
                                                {candidate.proposedPlatformPayoutPerPeriod(position.isFixedRetainerRateBased())}
                                                /{position.payout_frequency === 'weekly' ? 'wk' : 'mth'}
                                            </span>
                                        </h2>
                                        <p className="font-medium">{position.payoutFrequencyLabel()}</p>
                                    </>
                                ) : (
                                    <HideTalentPricingTooltip />
                                )}
                            </div>
                        ) : (
                            <div className="selected-rate">
                                <h4>Accepted Cost</h4>
                                <h2>
                                    {auth.canViewTalentPricing() ? (<span>{candidate.proposedPlatformCost()}</span>) : <HideTalentPricingTooltip />}
                                </h2>
                            </div>
                        ))
                    ) : (
                        candidate.isFixedRetainer() ? (
                            <div className="selected-rate">
                                <h4>Proposed Cost</h4>

                                {auth.canViewTalentPricing() ? (
                                    <>
                                        <h2>
                                            {(candidate.hasProposalForClient() && candidate.hasSubmittedProposal() && candidate.isFixedRetainerProposalValid(position.isFixedRetainerRateBased())) ? (
                                                <span>
                                                    {candidate.proposedPlatformPayoutPerPeriod(position.isFixedRetainerRateBased())}
                                                    /{position.payout_frequency === 'weekly' ? 'wk' : 'mth'}
                                                </span>
                                            ) : "Pending"}
                                        </h2>
                                        <p className="font-medium">{position.payoutFrequencyLabel()}</p>
                                    </>
                                ) : (
                                    <HideTalentPricingTooltip />
                                )}
                            </div>
                        ) : (
                            candidate.isFixedFee() ? (
                                <React.Fragment>
                                    <div className="selected-rate">
                                        <h4>Proposed Price</h4>
                                        {auth.canViewTalentPricing() ? (
                                            <>
                                                <h2>
                                                    {candidate.hasProposalForClient() ? candidate.proposedPlatformCost() : "Pending"}
                                                </h2>
                                                <p className="font-medium">fixed fee</p>
                                            </>
                                        ) : (
                                            <HideTalentPricingTooltip />
                                        )}
                                    </div>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    {candidate.changedHourlyRate() ? (
                                        <React.Fragment>
                                            <div className="rate-change-container">
                                                <h4>{candidate.client_override_hourly_rate ? "Preferred Rate" : "Standard Rate"}</h4>
                                                <h2>
                                                    {auth.canViewTalentPricing() ? (<span>{candidate.standardPlatformRate()}/hr</span>) : <HideTalentPricingTooltip />}
                                                </h2>
                                            </div>
                                            <div className="selected-rate">
                                                <h4>Proposed Rate</h4>
                                                <h2>
                                                    {auth.canViewTalentPricing() ? (<span>{candidate.proposedPlatformRate()}/hr</span>) : <HideTalentPricingTooltip />}
                                                </h2>
                                            </div>
                                        </React.Fragment>
                                    ) : (
                                        <div className="selected-rate">
                                            <h4>
                                                {candidate.client_override_hourly_rate ? "Preferred Rate" : "Standard Rate"}
                                            </h4>
                                            <h2>
                                                {auth.canViewTalentPricing() ? (<span>{candidate.standardPlatformRate()}/hr</span>) : <HideTalentPricingTooltip />}
                                            </h2>
                                        </div>
                                    )}
                                </React.Fragment>
                            )
                        )
                    )}
                </div>
                {candidate.hasProposalForClient() && (
                    <button onClick={() => onRequest(() => assignCandidate())} disabled={isDisabled || project.isActive() || position.hasBeenFilled() || !candidate.hasSubmittedProposal()}
                        className="btn btn-primary select-hire-btn">
                        Select as Hire
                    </button>
                )}
                {!candidate.hasProposalForClient() && (

                    <button disabled={true}
                        className="btn btn-outline-dark select-hire-btn select-unavailable">
                        {candidate.statusIsWithdrawn() && "No Longer Interested"}

                        {candidate.statusIsRejected() && <b>Not Interested</b>}

                        {candidate.statusIsPending() && "Waiting for Proposal"}
                    </button>
                )}
                {!project.isActive() && !position.hasBeenFilled() && (
                    <Dropdown menu={{
                        items: [
                            {
                                key: 'remove',
                                label: 'Remove from shortlist',
                                disabled: isDisabled
                            }, {
                                key: 'profile',
                                label: 'View Profile',
                                disabled: isDisabled
                            }
                        ],
                        onClick: async ({ item, key, keyPath, domEvent }) => {
                            // await TB.wait(200)
                            switch (key) {
                                case 'remove':
                                    return onRequest(() => removeFromCandidates())
                                case 'profile':
                                    return navigate({
                                        pathname: `talent-profiles/${candidate.id}`,
                                        search: position?.id ? `?role=${position?.id}` : ''
                                    })
                            }

                        }
                    }} trigger={['click']} placement="bottomRight">
                        <button disabled={isDisabled} className="btn btn-outline-dark min-h-[45px]">
                            <p><i className="fas fa-ellipsis-v" /></p>
                        </button>
                    </Dropdown>
                )}

            </div>
        </div>
    )
}

const ShortlistedCandidateList = ({ candidates, onRequest, isDisabled, ...props }) => {

    const { position, isDisabled: isPositionDisabled } = usePosition();
    const { project } = useProject();

    const [activeRow, setActiveRow] = useState(undefined);
    const [selectedTab, setSelectedTab] = useState('cover-letter');

    return (
        <div className="candidates-list">
            <Collapse accordion ghost activeKey={activeRow}
                expandIcon={({ isActive, panelKey, ...panelProps }) => {
                    const matchedCandidate = TB.find(candidates, ['id', parseInt(panelKey)]);

                    if (!matchedCandidate) return null;

                    if (matchedCandidate.declinedPosition()) {
                        return (
                            <StopOutlined className="text-slate-700 opacity-[.8]" />
                        )
                    }
                    if (!matchedCandidate || !matchedCandidate.hasProposalForClient()) {
                        return (
                            <ClockCircleOutlined className="opacity-[.8] text-slate-400" />
                        )
                    }


                    return (
                        <RightOutlined className="clickable" onClick={async (e) => {
                            e.preventDefault()
                            if (String(activeRow) === String(panelKey)) {
                                return setActiveRow(undefined)
                            } else {
                                if (panelKey && !activeRow && selectedTab !== 'cover-letter') {
                                    await setSelectedTab('cover-letter')
                                }
                                return setActiveRow(panelKey)
                            }
                        }} rotate={isActive ? 90 : undefined} />
                    )

                }}
                onChange={async (key) => {
                    // console.log('onChange', key);
                }}>
                {TB.map(TB.filter(candidates, ['job_candidate.is_shortlisted', true]), (candidate, i) => (
                    <Collapse.Panel
                        key={candidate.id}
                        collapsible={'header'}
                        style={!candidate.hasProposalForClient() ? (candidate.declinedPosition() ? { background: 'rgba(211,216,220,0.8)' } : (candidate.statusIsPending() ? { background: '#F5F6F8' } : undefined)) : undefined}
                        header={(
                            <CandidateProvider candidate={candidate}>
                                <ShortlistedCandidateRow
                                    onRequest={onRequest}
                                    isActive={activeRow === candidate.id}
                                    onViewProposal={async (tab) => {
                                        if (tab && tab !== selectedTab) {
                                            await setSelectedTab(tab)
                                        }
                                        setActiveRow(candidate.id)
                                    }}
                                    isDisabled={isDisabled} />
                            </CandidateProvider>
                        )}>
                        <CandidateProvider candidate={candidate}>
                            <CandidateProposal
                                isDisabled={isDisabled}
                                position={position}
                                project={project}
                                minimal={true}
                                selectedTab={selectedTab}
                                onTabChange={setSelectedTab}
                                candidate={candidate}
                                canRequestInterview={false}
                                onRequest={onRequest}
                            />
                        </CandidateProvider>
                    </Collapse.Panel>
                ))}
            </Collapse>

        </div>
    )
}

export default ShortlistedCandidateList